/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { useEffect, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { navigate } from 'gatsby';
import BuildingScreen from '../../components/screens/BuildingScreen';
import HomeScreen from '../../components/screens/HomeScreen';
import QuestionScreen from '../../components/screens/QuestionScreen';
import ResultScreen from '../../components/screens/ResultScreen';
import SEO from '../../components/SEO';
import {
  Hero,
  heroesImages,
  heroLogosImages,
  heroCharacteristics,
  questionsAndAnswers,
  sketchImages,
  HeroCharacteristics,
  heroesOgImages,
  Gender,
} from '../../configs';
import { useGlobalState } from '../../state/globalStateContext';
import '../../styles/main.scss';
import { shuffle } from '../../utils/nodash';

import mainOpenGraphImage from '../../images/opengraph/main.png';
import { SITE_BASE_URL } from '../../constants';
import { getHeroFromAnswers, sendEvent } from '../../utils/utils';
import { Helmet } from 'react-helmet';

interface IndexPageProps {
  pageContext: {
    heroSlug?: string;
  };
}

let firstVisitHeroWithHeroSlug = true;

const IndexPage = ({ pageContext }: IndexPageProps): React.ReactElement => {
  const {
    openShareCard,
    currentScreen,
    setCurrentScreen,
    answers,
    setAnswers,
    selectedGender,
    setSelectedGender,
    resetState,
  } = useGlobalState();
  const [shuffledSketchImages, setShuffledSketchImages] = useState(shuffle(sketchImages));
  const [firstLoadFinished, setFirstLoadFinished] = useState(false);
  const skipNextUpdateHistory = useRef(false);
  const replaceNextState = useRef(true);

  let resultHero: Hero | null = null;
  let chosenHeroCharacteristics: HeroCharacteristics | null = null;
  let seoProps = {
    lang: 'en',
    title: 'Super Dev Quiz',
    shareTitle: 'Super Dev Quiz - Which Superhero Developer are you?',
    description: 'Take our 5 question quiz to find out!',
    imageUrl: SITE_BASE_URL + mainOpenGraphImage,
    url: 'https://www.superdevquiz.com/',
  };
  if (currentScreen === 'result' || currentScreen === 'building') {
    resultHero = getHeroFromAnswers(answers);
  }
  if (currentScreen === 'result') {
    chosenHeroCharacteristics = heroCharacteristics[resultHero!];
    seoProps = {
      ...seoProps,
      title: chosenHeroCharacteristics.name + ' - Super Dev Quiz',
      shareTitle: "I'm " + chosenHeroCharacteristics.name + ' - Which Superhero Developer are you?',
      description: chosenHeroCharacteristics.ogDescription,
      imageUrl: SITE_BASE_URL + heroesOgImages[resultHero!][selectedGender!],
      url: 'https://www.superdevquiz.com/' + resultHero!.toLowerCase() + '-' + selectedGender + '/',
    };
  } else if (pageContext.heroSlug) {
    const [lowercaseSeoHero, seoGender] = pageContext.heroSlug.toLowerCase().split('-') as [
      string,
      Gender,
    ];
    const seoHero = {
      bugsmasher: 'bugSmasher',
      codeguardian: 'codeGuardian',
      hacker: 'hacker',
      jester: 'jester',
    }[lowercaseSeoHero] as Hero;
    const seoHeroCharacteristics = heroCharacteristics[seoHero];
    seoProps = {
      ...seoProps,
      title: 'Super Dev Quiz',
      shareTitle: "I'm " + seoHeroCharacteristics.name + ' - Which Superhero Developer are you?',
      description: seoHeroCharacteristics.ogDescription,
      imageUrl: SITE_BASE_URL + heroesOgImages[seoHero][seoGender],
      url: 'https://www.superdevquiz.com/' + pageContext.heroSlug + '/',
    };
  }

  function onPopState(e) {
    e.preventDefault();
    unstable_batchedUpdates(() => {
      if (e.state) {
        skipNextUpdateHistory.current = true;
        setCurrentScreen(e.state.currentScreen || 'home');
        setAnswers(e.state.answers || []);
        setSelectedGender(e.state.selectedGender || null);
      }
    });
  }

  function updateHistory(shouldReplaceState?: boolean, url?: string) {
    if (skipNextUpdateHistory.current) {
      skipNextUpdateHistory.current = false;
      return;
    }
    const newState = {
      currentScreen,
      answers,
      selectedGender,
    };
    // We need this url test because IE is very stupid and its behaviour is
    // different when passing the 3rd parameter as undefined than not passing it.
    if (url) {
      if (shouldReplaceState) {
        window.history.replaceState(newState, '', url);
      } else {
        window.history.pushState(newState, '', url);
      }
    } else {
      if (shouldReplaceState) {
        window.history.replaceState(newState, '');
      } else {
        window.history.pushState(newState, '');
      }
    }
  }

  useEffect(() => {
    if (pageContext.heroSlug) {
      if (firstVisitHeroWithHeroSlug) {
        firstVisitHeroWithHeroSlug = false;
        const newState = { currentScreen: 'home', answers: [], selectedGender: null };
        navigate('/', {
          state: newState,
        });
      } else {
        window.history.back();
      }
    }
    window.onpopstate = onPopState;
    setFirstLoadFinished(true);
  }, []);

  useEffect(() => {
    updateHistory(replaceNextState.current);
    if (replaceNextState.current) {
      replaceNextState.current = false;
    }
  }, [currentScreen, answers.join(','), selectedGender]);

  return (
    <>
      {firstLoadFinished && (
        <Helmet>
          <link rel="preload" as="image" href="/assets/images/panel.png"></link>
          <link rel="preload" as="image" href="/assets/images/spikes-small.svg"></link>
          <link rel="preload" as="image" href="/assets/images/texture-corner.png"></link>
          {answers.length < questionsAndAnswers.length - 1 && (
            <link
              rel="preload"
              as="image"
              href={shuffledSketchImages[currentScreen === 'home' ? 0 : answers.length + 1]}
              media="(min-width: 1200px)"
            ></link>
          )}
          {currentScreen === 'building' && (
            <link rel="preload" as="image" href={heroLogosImages[resultHero!]}></link>
          )}
          {currentScreen === 'building' && (
            <link rel="preload" as="image" href={heroesImages[resultHero!].male}></link>
          )}
          {currentScreen === 'building' && (
            <link rel="preload" as="image" href={heroesImages[resultHero!].female}></link>
          )}
        </Helmet>
      )}
      <SEO {...seoProps} />
      {currentScreen === 'home' && (
        <HomeScreen
          onStartClick={() => {
            setCurrentScreen('question');
            sendEvent('StartQuiz');
            window.scrollTo(0, 0);
          }}
        ></HomeScreen>
      )}
      {currentScreen === 'question' && (
        <QuestionScreen
          sketchImage={shuffledSketchImages[answers.length]}
          currentQuestionNumber={answers.length + 1}
          totalQuestionsNumber={questionsAndAnswers.length}
          question={questionsAndAnswers[answers.length].question}
          answersWithKeys={questionsAndAnswers[answers.length].answersWithHeroes.map(
            ({ hero, answer }) => [hero, answer],
          )}
          onAnswerPick={answerKey => {
            const newAnswers = [...answers, answerKey];
            unstable_batchedUpdates(() => {
              setAnswers(newAnswers);
              if (newAnswers.length >= questionsAndAnswers.length) {
                setCurrentScreen('building');
              }
            });
            window.scrollTo(0, 0);
          }}
        ></QuestionScreen>
      )}
      {currentScreen === 'building' && (
        <BuildingScreen
          onGenderPick={gender => {
            const randomGender = (['male', 'female'] as const)[Math.floor(Math.random() * 2)];
            unstable_batchedUpdates(() => {
              const newGender = gender || randomGender;
              setSelectedGender(newGender);
              setCurrentScreen('result');
              sendEvent(
                'QuizComplete',
                {
                  answer1: answers[0],
                  answer2: answers[1],
                  answer3: answers[2],
                  answer4: answers[3],
                  answer5: answers[4],
                  hero: resultHero!,
                  gender: newGender,
                  chosenGender: gender || 'either',
                  genderedHero: resultHero + '-' + newGender,
                },
                true,
              );
            });
            window.scrollTo(0, 0);
          }}
        ></BuildingScreen>
      )}
      {currentScreen === 'result' && (
        <ResultScreen
          heroSlug={resultHero!}
          heroGender={selectedGender!}
          heroName={chosenHeroCharacteristics!.name}
          maleHeroImage={heroesImages[resultHero!].male}
          femaleHeroImage={heroesImages[resultHero!].female}
          heroLogoImage={heroLogosImages[resultHero!]}
          superPower={chosenHeroCharacteristics!.superPower}
          weakness={chosenHeroCharacteristics!.weakness}
          description={chosenHeroCharacteristics!.description}
          onSwapGenderClick={() => {
            const newGender = selectedGender === 'male' ? 'female' : 'male';
            unstable_batchedUpdates(() => {
              setSelectedGender(newGender);
              replaceNextState.current = true;
            });
            sendEvent('ChangeGender', { hero: resultHero, newGender });
          }}
          onShareClick={() => {
            openShareCard('resultsPage');
          }}
          onRetryClick={() => {
            setShuffledSketchImages(shuffle(sketchImages));
            sendEvent('Retry', { fromHero: resultHero + '-' + selectedGender });
            resetState();
            window.scrollTo(0, 0);
          }}
        ></ResultScreen>
      )}
    </>
  );
};

export default IndexPage;
